import { useNavigate } from "react-router-dom";
import React, { useCallback, createContext, useContext, useMemo, useEffect, useRef, useState } from "react";
import { useWebSocket } from "WebSocketContext";

import {
  WATER_LEVEL_MANAGEMENT_SCHEMES,
  getWMRIndexFromArray,

  FAUNA_CLIMATE_VARIABILITY,
  FLORA_CLIMATE_VARIABILITY,
} from "utils/simulator";

import {
  type GameState,
  type RoleGroup,
  type MoneyTransferEvent,

  createGameActionsFromProposal,
  getAggrigateCosts,
  getNextGameState,
} from "utils/game_utils";

import {
  allRoles
} from "utils/game-information-constants";

import * as sim from "utils/simulator";

export type Player = {
  role: string;
  estate: string;
  playerName?: string;
  id: number;
}

type Stage = "game" | "vote" | "end";
type EstateBuckets = Record<"private" | "public", Record<string, any>>;
type RoleGroupActionMap = Record<string, Record<string, any>>;
const isEstateKey = (value: string): value is keyof EstateBuckets => value === "private" || value === "public";
type GameContextType = any;

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { playerId, socket, isConnected } = useWebSocket();
  const [canFill, setCanFill] = useState(false);

  const gameStarted = useRef(false)

  const MIN_DEBT = -4000;

  const [gameStartedState, setGameStartedState] = useState(false);
  const [gameId, setGameId] = useState("");
  const [hostId, setHostId] = useState(-1);

  const [amJoiningMidGame, setAmJoiningMidGame] = useState(false);

  const [isTimerPaused, setIsTimerPaused] = useState(false);

  const isAdmin = hostId === playerId && hostId > 0 && playerId > 0

  const [hostSelectedWM, setHostSelectedWM] = useState(-1);
  const [awaitingHostInput, setAwaitingHostInput] = useState(false);
  const [autoVote, setAutoVote] = useState(true);

  const [allPlayers, setAllPlayers] = useState<Map<number, Player>>(new Map());
  const [roleDataMap, setRoleDataMap] = useState<Record<string, RoleGroup>>({});
  const [gameStates, setGameStateHistory] = useState<Array<GameState>>([])
  const [proposalHistory, setProposalHistory] = useState<Array<any>>([]);

  const [wmVoteHistory, setWmVoteHistory] = useState<number[][]>([]);
  const [agrCostHistory, setAgrCostHistory] = useState<any[]>([])
  const [roleGroupDataHistory, setRoleGroupDataHistory] = useState<Array<Record<string, RoleGroup>>>([])
  const [selectedWmRegimes, setSelectedWmRegimes] = useState<number[]>([]);

  const [currentYear, setCurrentYear] = useState(1);
  const [yearCount, setYearCount] = useState(5);
  const [gameEnded, setGameEnded] = useState(false);

  const currentGameState = gameStates.length > 0 ? gameStates[0] : undefined;

  const [gameTimer, setGameTimer] = useState(5*60);
  const [voteTimer, setVoteTimer] = useState(7*60);

  const [wmVotes, setWmVotes] = useState<Array<number>>([0,0,0,0,0]);

  const selectedWmRegime = selectedWmRegimes.length > 0 ? selectedWmRegimes[selectedWmRegimes.length - 1] : -1

  const [roleGroupMapActions, setRoleGroupMapActions] = useState<Record<string, Record<string, any>>>({});
  const [combinedMapActions, setCombinedMapActions] = useState<Record<string, Record<string, any>>>(
    {
      "private": {},
      "public": {},
    }
  );
  const [combinedMuActions, setCombinedMuActions] = useState<Record<string, Record<string, any>>>(
    {
      "private": {},
      "public": {},
    }
  );
  const [combinedFaunaActions, setCombinedFaunaActions] = useState<Record<string, Record<string, any>>>(
    {
      "private": {},
      "public": {},
    }
  );

  const [proposedMoneyTransfers, setProposedMoneyTransfers] = useState<MoneyTransferEvent[]>([]);
  const [changeInHerdEvent, setChangeInHerdEvent] = useState<any>({});
  const [reservationMuActions, setReservationMuActions] = useState<Record<number, any>>({})

  const [currentStage, setCurrentStage] = useState<"game"|"vote"|"end">("game");

  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);



  function startTimer(startingTime: number) {
    setIsTimerPaused(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimeLeft(startingTime);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => Math.max(prev-1, 0))
    }, 1000);
  }

  function resumeTimer() {
    setIsTimerPaused(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => Math.max(prev-1, 0))
    }, 1000);
  }

  const playersArray = useMemo(() => {
    return Array.from(allPlayers, ([pId, ply]) => ({ ...ply }))
  }, [allPlayers])

  const hostIsPlaying = allPlayers.has(hostId);

  const playerInfo = useMemo(() => {
    if (playerId) {
      if (!allPlayers.has(playerId) && playerId === hostId) {
        return {
          role: "HOST",
          estate: "HOST",
          playerName: "HOST",
          id: hostId,
        }
      }

      return allPlayers.get(playerId);
    }
    return {
      role: "NONE",
      estate: "NONE",
      playerName: "",
      id: -1,
    }
  }, [allPlayers, playerId, hostId])

  const selfKey = `${playerInfo?.estate}-${playerInfo?.role}`
  const roleGroup = roleDataMap[selfKey]
  const selfRoleMoney = useMemo(() => {
    if (!roleGroup || !currentGameState) {
      return
    }
    const proposedMapActions = {...combinedMapActions["private"], ...combinedMapActions["public"]} as Record<string, any>
    const proposedMuActions = {...combinedMuActions["private"], ...combinedMuActions["public"]} as Record<string, any>
    const proposedFaunaActions = {...combinedFaunaActions["private"], ...combinedFaunaActions["public"]} as Record<string, any>

    const mapActionArray = Object.keys(proposedMapActions).map(function (key) {
        return proposedMapActions[key];
    });

    const muActionArray = Object.keys(proposedMuActions).map(function (key) {
        return proposedMuActions[key];
    });

    const faunaActionArray = Object.keys(proposedFaunaActions).map(function (key) {
        return proposedFaunaActions[key];
    });

    const reservationActionArray = Object.values(reservationMuActions)

    const proposal = {
      moneyTransfers: proposedMoneyTransfers,
      faunaActions: faunaActionArray,
      mapActions: mapActionArray,
      muActions: muActionArray,

      changeInHerdEvents: [], //changeInHerdEvent], //
      reservationMuActions: reservationActionArray, // Array.from(proposedReservationMuActions.values()),
    }

    const mmm = selectedWmRegime < 0 ? 3 : selectedWmRegime
    const agcost = getAggrigateCosts(
      proposal,
      currentGameState,
      WATER_LEVEL_MANAGEMENT_SCHEMES[mmm - 1],
      roleGroup,
      playerInfo?.estate ?? "",
      playerInfo?.role ?? "",
      FAUNA_CLIMATE_VARIABILITY[2],
      FLORA_CLIMATE_VARIABILITY[2],
      true,
    );

    return agcost?.startingBalance + agcost?.totalBalanceChange
  }, [
    roleGroup,
    currentGameState,
    selectedWmRegime,
    playerInfo,
    combinedMapActions,
    combinedMuActions,
    combinedFaunaActions,
    proposedMoneyTransfers,
    reservationMuActions,
    // changeInHerdEvent,
  ]);

  const allIds = useMemo(() => {
    let alo: any[] = []
    if (hostId > 0) {
      alo = [hostId]
      for (let i=0; i < playersArray.length; i++) {
        if (playersArray[i].id === hostId) {
          continue
        }
        alo.push(playersArray[i].id)
      }
    }
    return alo
  }, [playersArray, hostId])

  const sendPlayersToVoteRoom = useCallback(() => {
    setWmVotes([0,0,0,0,0])
    setCurrentStage("vote");
    const mes = JSON.stringify({
      kind: "GoToVoteRoom",
      toIds: allIds,
    });
    socket?.send( mes );

    const mess = JSON.stringify({
      kind: "UpdateTimer",
      toIds: allIds,
      newTime: voteTimer,
    });

    socket?.send( mess );
  }, [socket, allIds])


  function getHighestVotedIndex(votes: Array<number>) {
    let maxIndex = 0;
    let maxValue = votes[0];
  
    for (let i = 1; i < votes.length; i++) {
      if (votes[i] > maxValue) {
        maxValue = votes[i];
        maxIndex = i;
      }
    }
  
    return maxIndex;
  }

  /// swm ranges from 0-4
  const sendPlayersBackToGame = useCallback((props={useAutoVote: true, swmIndex:-1 }) => {
    setAwaitingHostInput(false);
    // Choose the wm regime
    let newWmRegime = props?.swmIndex; //getHighestVotedIndex(wmVotes);
    if (props?.useAutoVote) {
      newWmRegime = getHighestVotedIndex(wmVotes);
    }

    setWmVoteHistory((prev) => [...prev, wmVotes])

    calculateNextYear(newWmRegime+1);

    setCurrentStage("game");
    const mes = JSON.stringify({
      kind: "GoBackToGame",
      toIds: allIds,
    });
    socket?.send( mes );

    const mess = JSON.stringify({
      kind: "UpdateTimer",
      toIds: allIds,
      newTime: gameTimer,
    });

    socket?.send( mess );
  }, [socket, allIds, wmVotes])

  const calculateNextYear = useCallback((waterRegime: number) => {
    if (!currentGameState) {
      return
    }

    const proposedMapActions = {...combinedMapActions["private"], ...combinedMapActions["public"]} as Record<string, any>
    const proposedMuActions = {...combinedMuActions["private"], ...combinedMuActions["public"]} as Record<string, any>
    const proposedFaunaActions = {...combinedFaunaActions["private"], ...combinedFaunaActions["public"]} as Record<string, any>

    const mapActionArray = Object.keys(proposedMapActions).map(function (key) {
        return proposedMapActions[key];
    });

    const muActionArray = Object.keys(proposedMuActions).map(function (key) {
        return proposedMuActions[key];
    });

    const faunaActionArray = Object.keys(proposedFaunaActions).map(function (key) {
        return proposedFaunaActions[key];
    });

    const reservationActionArray = Object.values(reservationMuActions)

    const proposal = {
      moneyTransfers: proposedMoneyTransfers,
      faunaActions: faunaActionArray,
      mapActions: mapActionArray,
      muActions: muActionArray,

      changeInHerdEvents: [changeInHerdEvent], //
      reservationMuActions: reservationActionArray, // Array.from(proposedReservationMuActions.values()),
    }

    const newGameActions = createGameActionsFromProposal(proposal, waterRegime - 1); // Subtract 1 for index
    const newGameState = getNextGameState(currentGameState, newGameActions);

    const newRoleData = {...roleDataMap}
    const newCosts: Record<string, any> = {}

    const flv = sim.getFloraVariability(currentYear -1); // year is 1 indexed so we subtract
    const fuv = sim.getFloraVariability(currentYear -1);
    // Calculate changes for all the role groups
    for (let key in newRoleData) {
      const [estate, role] = key.split("-");

      const roleGroup = { ...newRoleData[key] }
      console.log("CHECK THESE MONEY TRANSFERS : " + proposedMoneyTransfers);
      let costs = getAggrigateCosts(
        proposal,
        newGameState,

        WATER_LEVEL_MANAGEMENT_SCHEMES[waterRegime-1],

        roleGroup,
        estate,role,
        fuv,flv
      )

      newCosts[key] = costs

      roleGroup.money += costs.totalBalanceChange;
      roleGroup.totalHuntingBag += costs.huntingBag;
      roleGroup.herdSize = costs.newHerdSize;

      newRoleData[key] = roleGroup
    }

    const newStuff = JSON.stringify({
      kind: "NewYearData",
      toIds: allIds,
      gameState: newGameState,
      allRoleGroups: newRoleData,
      agrCosts: newCosts,
      selectedWmRegime: waterRegime,
      newProposal: proposal,
    })

    socket?.send(newStuff)
  }, [
    changeInHerdEvent,
    allIds,
    currentYear,
    roleDataMap,
    currentGameState,
    combinedMapActions,
    combinedMuActions,
    combinedFaunaActions,
    proposedMoneyTransfers,
    reservationMuActions,
  ])

  useEffect(() => {
    if (gameEnded) return;

    if (timeLeft == 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimeLeft(-1);
      if (isAdmin) {
        if (currentStage === "game") {
          sendPlayersToVoteRoom();
        } else {
          if (autoVote) {
            sendPlayersBackToGame();
          } else {
            setAwaitingHostInput(true);
            if (hostSelectedWM > -1) {
              sendPlayersBackToGame({
                swmIndex: hostSelectedWM - 1,
                useAutoVote: false,
              });
            }
          }
        }
      }
    }
  }, [
    hostSelectedWM,
    autoVote,
    gameEnded,
    timeLeft,
    isAdmin,
    currentStage,
  ])

  useEffect(() => {
    if (awaitingHostInput && hostSelectedWM > -1) {
      sendPlayersBackToGame({
        swmIndex: hostSelectedWM - 1,
        useAutoVote: false,
      });
    }
  }, [awaitingHostInput, hostSelectedWM])

  const publicIds = useMemo(() => {
    return playersArray.filter((ply) => ply.estate === "public").map((ply) => ply.id)
  }, [playersArray])

  const privateIds = useMemo(() => {
    return playersArray.filter((ply) => ply.estate === "private").map((ply) => ply.id)
  }, [playersArray])

  const createRoleDataMap = useCallback(() => {
    const  roleGroups: Record<string, RoleGroup> = {}
    for (let i=0; i < playersArray.length; i++) {
      const ply = playersArray[i];
      if (!roleGroups.hasOwnProperty(`${ply.estate}-${ply.role}`)) {
        const roleData = allRoles[ allRoles.findIndex((elem) => {
          return elem.role === ply.role && elem.estate === ply.estate;
        }) ];

        // Set the inital conditions for that role
        const defaultRoleGroupData : RoleGroup = {
          money: roleData?.initialMoney || 0,
          startingRoleData: roleData,
          totalHuntingBag: 0,// hunting bag starts at zero
          herdSize: roleData?.initialHerdSize || 0,
          subsidy:roleData?.initialSubsidies || 0,
        }
        roleGroups[`${ply.estate}-${ply.role}`] = defaultRoleGroupData
      }
    }

    // Send players the role group data
    const mes = JSON.stringify({
      kind: "InitialRoleGroupData",
      toIds: allIds,
      initialRoleGroups: roleGroups,
    });
    socket?.send( mes );
  }, [playersArray, allIds, socket, allRoles])


  const entireGameState = useMemo(() => {
    return {
      proposalHistory: proposalHistory,
      gameId: gameId,
      hostId: hostId,

      changeInHerdEvent: changeInHerdEvent,

      allPlayers: playersArray,
      roleGroupDataHistory: roleGroupDataHistory,

      gameStates: gameStates,
      agrCostHistory: agrCostHistory,
      selectedWmRegimes: selectedWmRegimes,

      combinedMapActions: combinedMapActions,
      combinedMuActions: combinedMuActions,
      combinedFaunaActions: combinedFaunaActions,
      proposedMoneyTransfers: proposedMoneyTransfers,
      timeLeft: timeLeft,
      currentStage: currentStage,
      currentYear: currentYear,

      reservationMuActions: reservationMuActions,
    }
  }, [
    gameId,
    hostId,

    changeInHerdEvent,

    playersArray,
    roleGroupDataHistory,

    gameStates,
    agrCostHistory,
    selectedWmRegimes,

    combinedMapActions,
    combinedMuActions,
    combinedFaunaActions,
    proposedMoneyTransfers,
    timeLeft,
    currentStage,
    currentYear,

    reservationMuActions,
    proposalHistory,
  ])

  function refillGameContext(entireGameState: any) {
    setGameId(entireGameState.gameId)
    setHostId(entireGameState.hostId)

    const nm = new Map<number, Player>();
    for (let i=0; i < entireGameState.allPlayers.length; i++) {
      const plyId = entireGameState.allPlayers[i].id;
      nm.set( plyId, entireGameState.allPlayers[i] )
    }
    setAllPlayers(nm);

    setRoleGroupDataHistory(entireGameState.roleGroupDataHistory)

    if (entireGameState.roleGroupDataHistory.length > 0) {
      setRoleDataMap(entireGameState.roleGroupDataHistory[entireGameState.roleGroupDataHistory.length - 1]);
    }

    setProposalHistory(entireGameState.proposalHistory);

    setGameStateHistory(entireGameState.gameStates)
    setAgrCostHistory( entireGameState.agrCostHistory)
    setSelectedWmRegimes( entireGameState.selectedWmRegimes)

    setCombinedMapActions( entireGameState.combinedMapActions)
    setCombinedMuActions( entireGameState.combinedMuActions)
    setCombinedFaunaActions( entireGameState.combinedFaunaActions)
    setChangeInHerdEvent(entireGameState.changeInHerdEvent);
    setProposedMoneyTransfers( entireGameState.proposedMoneyTransfers)

    setReservationMuActions(entireGameState.reservationMuActions);

    startTimer(entireGameState.timeLeft)

    setCurrentStage( entireGameState.currentStage)
    setCurrentYear( entireGameState.currentYear)
  }

  function refillGameContextFromFile(jsonData: any) {
    // setGameId(entireGameState.gameId)
    // setHostId(entireGameState.hostId)

    console.log(playerId);
    setHostId(playerId);

    const nm = new Map<number, Player>();
    for (let i=0; i < jsonData.players.length; i++) {
      const plyId = jsonData.players[i].id;
      nm.set( plyId, jsonData.players[i] )
    }
    setAllPlayers(nm);

    setRoleGroupDataHistory(jsonData.roleGroupDataHistory)

    if (jsonData.roleGroupDataHistory.length > 0) {
      setRoleDataMap(jsonData.roleGroupDataHistory[jsonData.roleGroupDataHistory.length - 1]);
    }

    // setProposalHistory(jsonData.proposalHistory);

    setGameStateHistory(jsonData.gameStateHistory)
    setAgrCostHistory( jsonData.agrCostHistory)

    setSelectedWmRegimes( jsonData.selectedWmRegimes)

    // setCombinedMapActions( entireGameState.combinedMapActions)
    // setCombinedMuActions( entireGameState.combinedMuActions)
    // setCombinedFaunaActions( entireGameState.combinedFaunaActions)
    // setChangeInHerdEvent(entireGameState.changeInHerdEvent);
    // setProposedMoneyTransfers( entireGameState.proposedMoneyTransfers)

    // setReservationMuActions(entireGameState.reservationMuActions);

    // startTimer(entireGameState.timeLeft)

    // setCurrentStage( entireGameState.currentStage)
    // setCurrentYear( entireGameState.currentYear)
  }

  useEffect(() => {
    if (!isAdmin) return

    if (yearCount > 0 && currentYear > yearCount) {
      socket?.send(JSON.stringify({
        toIds: allIds,
        kind: "EndGame",
      }))

    }
  }, [ currentYear, yearCount, isAdmin, allIds, socket])


  useEffect(() => {
    if (amJoiningMidGame && playerInfo?.role && playerInfo?.estate) {
      // By the time you're here you should know what the player info is
      socket?.send(JSON.stringify({
        kind: "PlayerJoinMidGame",
        toIds: allIds,
        playerInfo: playerInfo,
      }))
      setAmJoiningMidGame(false);
    }
  }, [playerInfo, amJoiningMidGame, allIds])

  const navigate = useNavigate()


  // console.log(roleGroupMapActions);
  // console.log(combinedMapActions);
  useEffect(() => {
    const newCombinedMapActions: Record<string, Record<string, any>> = {
      "private": {},
      "public": {},
    }

    for (const [rg, mp] of Object.entries(roleGroupMapActions)) {
      const estate = rg.split("-")[0]
      if (!isEstateKey(estate)) {
        continue;
      }
      for (const [cellLoc, action] of Object.entries(mp)) {
        const oldProposal = newCombinedMapActions[`${estate}`]
        const existingAction = oldProposal[`${action.location.x}-${action.location.y}`]

        // console.log(action);
        // COMPARE AND CONTRAST SHIT HERE
        if (existingAction) {
          newCombinedMapActions[`${estate}`][`${action.location.x}-${action.location.y}`] = existingAction;
          
          if (existingAction.actionKind === "clearing") {
            newCombinedMapActions[`${estate}`][`${action.location.x}-${action.location.y}`] = existingAction;
          } else if(action.actionKind === "clearing") {
            newCombinedMapActions[`${estate}`][`${action.location.x}-${action.location.y}`] = action;
          } else if (existingAction.actionKind === "grazing" && action.actionKind === "cut") {
            newCombinedMapActions[`${estate}`][`${action.location.x}-${action.location.y}`] = existingAction;
          } else if (existingAction.actionKind === "cut" && action.actionKind === "grazing") {
            newCombinedMapActions[`${estate}`][`${action.location.x}-${action.location.y}`] = action;
          }
        } else {
          newCombinedMapActions[`${estate}`][`${action.location.x}-${action.location.y}`] = action;
          // newProposedMap[`${action.location.x}-${action.location.y}`] = obj;
        }

      }
    }

    setCombinedMapActions(newCombinedMapActions)
  },[roleGroupMapActions])

  const onMessage = useCallback((event: { data: string; }) => {
    if (event.data) {
      const message = JSON.parse(event.data);
      console.log(message);

      if (message.kind === "Die") {
        socket?.close();
        navigate("/kicked");
      }

      if (message.kind === "Pause") {
        setIsTimerPaused(true);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }

      if (message.kind === "Resume") {
        resumeTimer();
      }

      if (message.kind === "EndGame") {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setCurrentStage("end");
        setGameEnded(true);
      }

      if (message.kind === "UpdateChangeInHerd") {
        setChangeInHerdEvent(message.changeInHerdEvent)
      }

      if (message.kind === "UpdateReservationMuActions") {
        setReservationMuActions(message.reservationMuActions);
      }

      if (message.kind === "GoToVoteRoom") {
        setCurrentStage("vote");
      }
      if (message.kind === "GoBackToGame") {
        setCurrentStage("game");
      }

      if (message.kind === "GameCreated") {
        if (isAdmin) {
          setAllPlayers(new Map());
        }
      }

      if (message.kind === "OnGoingGameData") {
        gameStarted.current = true
        setGameStartedState(true);
        refillGameContext(message.entireGameState);
        setAmJoiningMidGame(true);
      }

      if (message.kind === "PlayerDisconnected") {
        setAllPlayers(prev => {
          const n = new Map(prev)
          n.delete(message.playerId);
          return n
        })
      }

      if (message.kind === "RemoveHostFromPlaying") {
        setAllPlayers(prev => {
          const n = new Map(prev)
          n.delete(message.playerId);
          return n
        })
      }

      if (message.kind === "JoinedGame") {
        setAllPlayers(prev => new Map(prev.set( message.playerId, {
          id: message.playerId,
          role: "NONE",
          estate: "NONE",
          playerName: "",
        })))

        if (isAdmin && gameStarted.current) {
          // message the other guy to start the game

          socket?.send(JSON.stringify({
            kind: "OnGoingGameData",
            toIds: [message.playerId],
            entireGameState: entireGameState,
          }))
        }
      }

      if (message.kind === "UpdatePlayerInfo") {
        setAllPlayers(prev => new Map(prev.set( message.playerId, {
          ...prev.get(message.playerId), ...message.newData
        })))
      }

      if (message.kind === "UpdateAllPlayersInfo") {
        const nm = new Map<number, Player>()
        for (let i=0; i < message.allPlayers.length; i++) {
          const plyId = message.allPlayers[i].id;
          nm.set( plyId, message.allPlayers[i] )
        }
        setAllPlayers(nm);
      }
     
      if (message.kind === "StartingGame") {
        gameStarted.current = true
        setGameStartedState(true);
        setGameStateHistory([message.initialGameState])
        if (isAdmin) {
          createRoleDataMap();
        }
        startTimer(message.gameTime);
      }

      if (message.kind === "UpdateTimer") {
        startTimer(message.newTime);
      }

      if (message.kind === "InitialRoleGroupData") {
        setRoleDataMap(message.initialRoleGroups)
        setRoleGroupDataHistory([message.initialRoleGroups])
      }

      if (message.kind === "UpdateMuActions") {
        const newMuActions = message.muActions;

        const newProposedMap: Record<string, any> = {} 

        for (let i = 0; i < newMuActions.length; i++) {
          const obj = newMuActions[i]
          newProposedMap[`${obj.managementUnit}`] = obj;
        }
        setCombinedMuActions((prev) => {
          return {
            ...prev,
            [`${message.estate}`]: newProposedMap
          }
        })
        return
      }
      if (message.kind === "UpdateDikeMU") {
        setGameStateHistory((prev) => {
          let rr = [...prev]
          if (rr.length > 0) {
            const cc = {...rr[0]}
            cc.muData[message.managementUnit].wmRegime = WATER_LEVEL_MANAGEMENT_SCHEMES[message.wmRegime - 1]

            rr[0] = cc
          }

          return rr
        })
        // currentGameState.muData[message?.mu].wmRegime = WATER_LEVEL_MANAGEMENT_SCHEMES[wmR - 1]
      }

      if (message.kind === "UpdateRoleMapActions") {
        const newMapActions = message.mapActions;

        setRoleGroupMapActions((prev) => {
          const oldRoleGroupMapActions = {...prev}

          const newProposedMap: Record<string, any> = {}
          for (let i = 0; i < newMapActions.length; i++) {
            const obj = newMapActions[i]
            newProposedMap[`${obj.location.x}-${obj.location.y}`] = obj;
          }

          oldRoleGroupMapActions[`${message.estate}-${message.role}`] = newProposedMap
          return oldRoleGroupMapActions
        })
      }

      if (message.kind === "UpdateMapActions") {
        const newMapActions = message.mapActions;

        setCombinedMapActions((prev) => {
          const oldMap = {...prev}
          const oldProposal = oldMap[`${message.estate}`]

          const newProposedMap = {...oldProposal} 
          for (let i = 0; i < newMapActions.length; i++) {
            const obj = newMapActions[i]
            const existingAction = oldProposal[`${obj.location.x}-${obj.location.y}`]
            // COMPARE AND CONTRAST SHIT HERE
            if (existingAction) {
              newProposedMap[`${obj.location.x}-${obj.location.y}`] = existingAction;
            } else {
              newProposedMap[`${obj.location.x}-${obj.location.y}`] = obj;
            }
          }

          return {
            ...oldMap,
            [`${message.estate}`]: newProposedMap
          }
        })

        return
      }

      if (message.kind === "UpdateFaunaActions") {
        const newMapActions = message.faunaActions;
        const newProposedMap: Record<string, any> = {} 
        for (let i = 0; i < newMapActions.length; i++) {
          const obj = newMapActions[i]
          newProposedMap[`${obj.location.x}-${obj.location.y}`] = obj;
        }

        setCombinedFaunaActions((prev) => {
          return {
            ...prev,
            [`${message.estate}`]: newProposedMap
          }
        })
        return
      }

      if (message.kind === "WMVote") {
        if (!isAdmin) {
          return
        }
        setWmVotes((prev) => {
          const n = [...prev]
          n[message.wmRegime - 1] += 1
          return n
        })
      }

      if (message.kind === "NewYearData") {
        setGameStateHistory((prev) => [message.gameState, ...prev])
        setRoleDataMap(message.allRoleGroups);


        setProposalHistory((prev) => [...prev, message.newProposal]);

        setRoleGroupDataHistory((prev) => [...prev, message.allRoleGroups])
        setAgrCostHistory((prev) => [...prev, message.agrCosts])

        setCombinedMapActions({ "private": {}, "public": {}, });
        setRoleGroupMapActions({});
        setCombinedMuActions({ "private": {}, "public": {}, });
        setCombinedFaunaActions({ "private": {}, "public": {}, });
        setProposedMoneyTransfers([]);
        setReservationMuActions({})
        setCurrentYear((prev) => prev + 1);
        setSelectedWmRegimes((prev) => [...prev,message.selectedWmRegime])
      }

      if (message.kind === "MoneyTransferEvent") {
        setProposedMoneyTransfers((prev) => [
          ...prev, {
            authorId: message.authorId,
            amountTransfer: message.amountTransfer,
            fromGroupName: message.fromGroupName,
            toGroupName: message.toGroupName,
          }
        ])
        
      }

      if (message.kind === "MoneyTransferEvents") {
        if (isAdmin) return;
        setProposedMoneyTransfers(message.moneyTransfers)
      }
    }
  }, [
    createRoleDataMap,
    isAdmin,
    entireGameState,
    // currentGameState,
  ])

  useEffect(() => {
    if (isAdmin) {
      socket?.send(JSON.stringify({
        kind: "MoneyTransferEvents",
        toIds: allIds, 
        moneyTransfers: proposedMoneyTransfers,
      }))
    }
  }, [isAdmin, proposedMoneyTransfers])


  useEffect(() => {
    if (!isAdmin) {
      return
    }

    if (currentStage !== "vote") {
      return
    }

    const sum = wmVotes.reduceRight((acc, cur) => acc + cur, 0)

    if ((playersArray.length - sum) === 0) {
      const mes = JSON.stringify({
        kind: "UpdateTimer",
        toIds: allIds,
        newTime: 1,
      });

      socket?.send( mes );
    }

    socket?.send(JSON.stringify({
      kind: "VoteCounts",
      toIds: allIds,
      voteCount: sum,
    }))
  }, [wmVotes, playersArray, isAdmin, currentStage])


  useEffect(() => {
    if (isAdmin && hostId > 0) {
      const mes = JSON.stringify({
        kind: "UpdateAllPlayersInfo",
        toIds: allIds.filter(ii => ii !== hostId),
        allPlayers: playersArray,
      });

      socket?.send( mes );
    }
  }, [isAdmin, playersArray, socket, allIds, hostId])

  useEffect(() => {
    if (!socket) return;
    socket.addEventListener("message", onMessage);

    return () => {
      if (socket) {
        socket.removeEventListener("message", onMessage);
      }
    }
  }, [socket, onMessage]);

  useEffect(() => {
    setIsTimerPaused(false);
    setGameEnded(false);
    gameStarted.current = false
    setGameStartedState(false);
    setWmVoteHistory([]);
    setAmJoiningMidGame(false);


    setRoleGroupMapActions({});
    setCombinedMapActions({
      "private": {},
      "public": {},
    })
    setCombinedMuActions({
      "private": {},
      "public": {},
    })
    setCombinedFaunaActions({
      "private": {},
      "public": {},
    })

    setProposedMoneyTransfers([]);
    setChangeInHerdEvent({});
    setReservationMuActions({});
  }, [gameId])

  function updateTimeLeft(newTime: number) {
    if (!isAdmin) {
      return
    }

    const mes = JSON.stringify({
      kind: "UpdateTimer",
      toIds: allIds,
      newTime: newTime,
    });
    socket?.send( mes );
  }

  function downloadGameData() {
    saveTemplateAsFile("GameData", JSON.stringify({
      currentYear: currentYear,
      players: playersArray,
      gameStateHistory: gameStates,

      wmVoteHistory: wmVoteHistory,
      agrCostHistory: agrCostHistory,
      roleGroupDataHistory: roleGroupDataHistory,
      selectedWmRegimes: selectedWmRegimes,
    }))
  }

  //  download file, no file size limit
  const saveTemplateAsFile = (filename:string, dataObjToWrite:any) => {
    const blob = new Blob([JSON.stringify(dataObjToWrite)], { type: "text/json" });
    const link = document.createElement("a");

    link.download = filename;
    link.href = window.URL.createObjectURL(blob);
    link.dataset.downloadurl = ["text/json", link.download, link.href].join(":");

    const evt = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true,
    });

    link.dispatchEvent(evt);
    link.remove()
  };


  return (
    <GameContext.Provider value={{
      minDebt: MIN_DEBT,
      selfRoleMoney: selfRoleMoney,

      currentYear: currentYear,
      gameId,
      setGameId,

      isAdmin,
      hostId,
      hostIsPlaying: hostIsPlaying,
      setHostId,

      players: playersArray,
      connectedIds: allIds,

      publicEstateIds: publicIds,
      privateEstateIds: privateIds,

      playerMap: allPlayers,
      
      playerInfo: playerInfo,

      currentGameState: currentGameState,
      roleGroups: roleDataMap,

      timeLeft: timeLeft,
      gameTimer: gameTimer,
      setGameTimer: setGameTimer,
      voteTimer: voteTimer,
      setVoteTimer: setVoteTimer,

      updateTimeLeft: updateTimeLeft,

      proposedGameMapActions: combinedMapActions,
      proposedGameMuActions: combinedMuActions,
      proposedGameFaunaActions: combinedFaunaActions,
      proposedMoneyTransfers: proposedMoneyTransfers,


      agrCostHistory: agrCostHistory,
      roleGroupDataHistory: roleGroupDataHistory,
      gameStateHistory: gameStates,

      selectedWmRegime: selectedWmRegime,

      gameStarted: gameStartedState,
      currentStage: currentStage,
      changeInHerdEvent: changeInHerdEvent,
      reservationMuActions: reservationMuActions,

      setYearCount: setYearCount,
      gameEnded: gameEnded,
      yearCount: yearCount,
      isTimerPaused: isTimerPaused,
      wmVotes: wmVotes,
      selectedWmRegimes: selectedWmRegimes,
      wmVoteHistory: wmVoteHistory,
      amJoiningMidGame: amJoiningMidGame,

      downloadGameData: downloadGameData,

      autoVote: autoVote,
      setAutoVote: setAutoVote,

      hostSelectedWM: hostSelectedWM,
      setHostSelectedWM: setHostSelectedWM,

      proposalHistory: proposalHistory,

      refillGameContextFromFile,
    }}>
      {children}
    </GameContext.Provider>
  );
};

export const useGameContext = () => {
  const context = useContext(GameContext);
  if (!context) throw new Error("useGameContext must be used inside GameContextProvider");
  return context;
};
